import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Permissions } from '../auth/rbac'
import { useAccess } from '../auth/useAccess'
import { api } from '../lib/api'
import { formatDateTimeMinute } from '../lib/date'
import ListContainer from '../ui/ListContainer'
import LibraryMappedImagesModal from '../ui/LibraryMappedImagesModal'
import { useToast } from '../ui/ToastProvider'
import MenuGuideButton from '../components/MenuGuideButton'

const statusLabel = (status) => {
  if (status === 'APPROVED') return '승인'
  if (status === 'REJECTED') return '반려'
  if (status === 'DRAFT') return '초안'
  return status || '-'
}

const typeLabel = (type) => {
  if (type === 'INFO') return '정보형'
  if (type === 'CONSULT') return '상담유도형'
  if (type === 'REMIND') return '리마인드형'
  return type || '-'
}

const isFallbackDraft = (draft) => {
  try {
    const flags = draft?.complianceFlagsJson ? JSON.parse(draft.complianceFlagsJson) : null
    if (flags?.manualOperatorDraft === true) return false
    if (flags?.fallbackTemplate === true) return true
  } catch (_) {
    // ignore invalid json
  }
  return draft?.degradedReason === 'NO_ISSUES_COLLECTED' || draft?.issueCandidateId == null
}

export default function MessageTemplatesPage() {
  const { show } = useToast()
  const { guard, allow } = useAccess()
  const [status, setStatus] = useState('')
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [generating, setGenerating] = useState(false)
  const [generationMode, setGenerationMode] = useState('BOTH')
  const [generationLlmModel, setGenerationLlmModel] = useState('')
  const [generationPolicies, setGenerationPolicies] = useState([])
  const [generationPolicySavingId, setGenerationPolicySavingId] = useState(null)
  const [detail, setDetail] = useState(null)
  const [lastFailureSummary, setLastFailureSummary] = useState(null)
  const [topicPolicies, setTopicPolicies] = useState([])
  const [policyEdits, setPolicyEdits] = useState({})
  const [policyLoading, setPolicyLoading] = useState(false)
  const [policySavingId, setPolicySavingId] = useState(null)
  const [customNewsTheme, setCustomNewsTheme] = useState('')
  const [customInsuranceTopic, setCustomInsuranceTopic] = useState('')
  const [customInsuranceTopicHint, setCustomInsuranceTopicHint] = useState('')
  const [manualTitle, setManualTitle] = useState('')
  const [manualContent, setManualContent] = useState('')
  const [manualCategory, setManualCategory] = useState('')
  const [manualTemplateType, setManualTemplateType] = useState('INFO')
  const [manualSaving, setManualSaving] = useState(false)
  const [topicInputsOpen, setTopicInputsOpen] = useState(false)
  const [manualDraftOpen, setManualDraftOpen] = useState(false)
  const [aiContentOpen, setAiContentOpen] = useState(false)
  const [aiContentType, setAiContentType] = useState('CARD_NEWS')
  const [aiContentTopic, setAiContentTopic] = useState('')
  const [aiContentTargetAgeGroup, setAiContentTargetAgeGroup] = useState('')
  const [aiContentTargetContext, setAiContentTargetContext] = useState('')
  const [aiContentTone, setAiContentTone] = useState('친절하고 쉬운 설명')
  const [aiContentChannel, setAiContentChannel] = useState('카카오/문자/랜딩 재사용')
  const [aiContentCallToAction, setAiContentCallToAction] = useState('')
  const [aiContentLlmModel, setAiContentLlmModel] = useState('')
  const [aiContentGenerating, setAiContentGenerating] = useState(false)
  const [detailTitleDraft, setDetailTitleDraft] = useState('')
  const [detailContentDraft, setDetailContentDraft] = useState('')
  const [detailCategoryDraft, setDetailCategoryDraft] = useState('')
  /** 이미지 자료실(gemini_generated_images.id) 연결 순서 */
  const [detailLinkedImageIds, setDetailLinkedImageIds] = useState([])
  const [libraryImagesForPicker, setLibraryImagesForPicker] = useState([])
  const [libraryPickerLoading, setLibraryPickerLoading] = useState(false)
  const [detailEditsSaving, setDetailEditsSaving] = useState(false)
  const [listPage, setListPage] = useState(0)
  const listPageSize = 20
  const [listTotalPages, setListTotalPages] = useState(0)
  const [listTotalElements, setListTotalElements] = useState(0)
  const [mappingImageModalIds, setMappingImageModalIds] = useState(null)
  const [selectedDraftIds, setSelectedDraftIds] = useState([])
  const [bulkAction, setBulkAction] = useState(null)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = { page: listPage, size: listPageSize }
      if (status) params.status = status
      const res = await api.get('/operator/message-templates/drafts', { params })
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      setItems(Array.isArray(payload?.content) ? payload.content : [])
      setSelectedDraftIds([])
      const tp = Number(payload?.totalPages)
      const te = Number(payload?.totalElements)
      setListTotalPages(Number.isFinite(tp) && tp >= 0 ? tp : 0)
      setListTotalElements(Number.isFinite(te) && te >= 0 ? te : (Array.isArray(payload?.content) ? payload.content.length : 0))
    } catch (_) {
      setError('메시지 템플릿 목록을 불러오지 못했습니다')
    } finally {
      setLoading(false)
    }
  }, [status, listPage, listPageSize])

  useEffect(() => {
    void reload()
  }, [reload])

  const toMonthWeightText = (weights) => {
    if (!weights || typeof weights !== 'object') return ''
    return Object.entries(weights)
      .sort((a, b) => Number(a[0]) - Number(b[0]))
      .map(([m, w]) => `${m}:${w}`)
      .join(',')
  }

  const parseMonthWeightText = (text) => {
    const out = {}
    const src = String(text || '').trim()
    if (!src) return out
    src.split(',').forEach((token) => {
      const [mRaw, wRaw] = token.split(':').map((v) => (v || '').trim())
      const m = Number(mRaw)
      const w = Number(wRaw)
      if (Number.isInteger(m) && m >= 1 && m <= 12 && Number.isFinite(w)) {
        out[String(m)] = w
      }
    })
    return out
  }

  const validatePolicyEdit = (edit) => {
    const errors = []
    const baseWeight = Number(edit?.baseWeight)
    if (!Number.isFinite(baseWeight) || !Number.isInteger(baseWeight)) {
      errors.push('기본가중치는 정수여야 합니다.')
    } else if (baseWeight < -100 || baseWeight > 100) {
      errors.push('기본가중치는 -100~100 범위여야 합니다.')
    }

    const cooldownDays = Number(edit?.cooldownDays)
    if (!Number.isFinite(cooldownDays) || !Number.isInteger(cooldownDays)) {
      errors.push('중복회피일은 정수여야 합니다.')
    } else if (cooldownDays < 0 || cooldownDays > 30) {
      errors.push('중복회피일은 0~30 범위여야 합니다.')
    }

    const src = String(edit?.monthWeightsText || '').trim()
    if (src) {
      const tokens = src.split(',').map((v) => v.trim()).filter(Boolean)
      const seen = new Set()
      for (const token of tokens) {
        const [mRaw, wRaw] = token.split(':').map((v) => (v || '').trim())
        const month = Number(mRaw)
        const weight = Number(wRaw)
        if (!(Number.isInteger(month) && month >= 1 && month <= 12 && Number.isFinite(weight))) {
          errors.push('월가중치는 `월:가중치` 형식(예: 1:2,9:2)으로 입력해야 합니다.')
          break
        }
        if (seen.has(month)) {
          errors.push('월가중치에 중복 월이 있습니다.')
          break
        }
        seen.add(month)
      }
    }
    return errors
  }

  const loadTopicPolicies = useCallback(async () => {
    setPolicyLoading(true)
    try {
      const res = await api.get('/operator/message-templates/topic-policies')
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      const list = Array.isArray(payload) ? payload : []
      setTopicPolicies(list)
      const edits = {}
      list.forEach((p) => {
        edits[p.id] = {
          title: p.title || '',
          enabled: !!p.enabled,
          baseWeight: p.baseWeight ?? 0,
          cooldownDays: p.cooldownDays ?? 7,
          monthWeightsText: toMonthWeightText(p.monthWeights),
        }
      })
      setPolicyEdits(edits)
    } catch (_) {
      show('세부정책 조회 실패', 'error')
    } finally {
      setPolicyLoading(false)
    }
  }, [show])

  useEffect(() => {
    void loadTopicPolicies()
  }, [loadTopicPolicies])

  const onGenerate = async () => {
    if (!allow.perm([Permissions.TEMPLATE_MANAGE])) {
      show('권한이 없습니다', 'error')
      return
    }
    setGenerating(true)
    try {
      const params = { days: 2, mode: generationMode }
      if (generationLlmModel) params.llmModel = generationLlmModel
      const body = {}
      const newsT = String(customNewsTheme || '').trim()
      const insT = String(customInsuranceTopic || '').trim()
      const insH = String(customInsuranceTopicHint || '').trim()
      if (newsT) body.customNewsTheme = newsT
      if (insT) body.customInsuranceTopic = insT
      if (insH) body.customInsuranceTopicHint = insH
      // 빈 객체여도 일부 환경에서 x-www-form-urlencoded 로 나가 @RequestBody(JSON) 과 충돌 → JSON 고정
      const jsonBody = JSON.stringify(Object.keys(body).length > 0 ? body : {})
      const res = await api.post(
        '/operator/message-templates/drafts/generate-manual',
        jsonBody,
        {
          params,
          headers: { 'Content-Type': 'application/json' },
        },
      )
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      setLastFailureSummary(payload?.failureSummary || null)
      const customBits = [
        payload?.customNewsThemeApplied ? '뉴스·직접주제' : null,
        payload?.customInsuranceTopicApplied ? '세부주제·직접입력' : null,
      ].filter(Boolean)
      const customSuffix = customBits.length > 0 ? ` [${customBits.join(', ')}]` : ''
      show(
        `초안 생성 완료: 총 ${payload?.createdDrafts ?? 0}건 (뉴스 ${payload?.createdNewsDrafts ?? 0} / 보험세부주제 ${payload?.createdInsuranceTopicDrafts ?? 0}) [뉴스:${payload?.newsLlmModel || '-'} / 세부:${payload?.insuranceTopicLlmModel || '-'}]${payload?.degradedMode ? ' (금감원체크: X)' : ''}${customSuffix}`,
        'success',
      )
      await reload()
    } catch (_) {
      show('초안 생성 실패', 'error')
    } finally {
      setGenerating(false)
    }
  }

  const onCreateManualDraft = async () => {
    if (!allow.perm([Permissions.TEMPLATE_MANAGE])) {
      show('권한이 없습니다', 'error')
      return
    }
    const title = String(manualTitle || '').trim()
    const content = manualContent ?? ''
    if (!title) {
      show('제목을 입력해 주세요.', 'error')
      return
    }
    if (!String(content).trim()) {
      show('본문을 입력해 주세요.', 'error')
      return
    }
    setManualSaving(true)
    try {
      const body = {
        title,
        content: String(content),
        templateType: manualTemplateType || 'INFO',
        category: String(manualCategory || '').trim() || null,
      }
      const res = await api.post('/operator/message-templates/drafts', body, {
        headers: { 'Content-Type': 'application/json' },
      })
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      show('직접 작성 초안이 등록되었습니다.', 'success')
      setManualTitle('')
      setManualContent('')
      setManualCategory('')
      setManualTemplateType('INFO')
      await reload()
      if (payload?.id != null) {
        await onOpenDetail(payload.id)
      }
    } catch (e) {
      const msg = e?.response?.data?.message
      show(typeof msg === 'string' ? msg : '직접 작성 초안 등록에 실패했습니다.', 'error')
    } finally {
      setManualSaving(false)
    }
  }

  const onGenerateAiContentDraft = async () => {
    if (!allow.perm([Permissions.TEMPLATE_MANAGE])) {
      show('권한이 없습니다', 'error')
      return
    }
    const topic = String(aiContentTopic || '').trim()
    if (!topic) {
      show('콘텐츠 주제를 입력해 주세요.', 'error')
      return
    }
    setAiContentGenerating(true)
    try {
      const body = {
        contentType: aiContentType,
        topic,
        targetAgeGroup: String(aiContentTargetAgeGroup || '').trim() || null,
        targetContext: String(aiContentTargetContext || '').trim() || null,
        tone: String(aiContentTone || '').trim() || null,
        channel: String(aiContentChannel || '').trim() || null,
        callToAction: String(aiContentCallToAction || '').trim() || null,
        llmModel: aiContentLlmModel || null,
      }
      const res = await api.post('/operator/message-templates/drafts/generate-content', body, {
        headers: { 'Content-Type': 'application/json' },
      })
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      show(`AI 콘텐츠 초안을 생성했습니다.${payload?.ruleBasedFallback ? ' (규칙기반)' : ''}`, 'success')
      setAiContentTopic('')
      setAiContentTargetAgeGroup('')
      setAiContentTargetContext('')
      setAiContentCallToAction('')
      await reload()
      if (payload?.draftId != null) {
        await onOpenDetail(payload.draftId)
      }
    } catch (e) {
      const msg = e?.response?.data?.message
      show(typeof msg === 'string' ? msg : 'AI 콘텐츠 초안 생성에 실패했습니다.', 'error')
    } finally {
      setAiContentGenerating(false)
    }
  }

  const normalizeLinkedIds = (raw) => {
    if (!Array.isArray(raw)) return []
    const out = []
    for (const x of raw) {
      const n = Number(x)
      if (Number.isFinite(n) && n >= 1) out.push(Math.trunc(n))
    }
    return out
  }

  const syncDetailFromServer = async (id) => {
    if (detail?.id !== id) return
    try {
      const res = await api.get(`/operator/message-templates/drafts/${id}`)
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      setDetail(payload)
      setDetailTitleDraft(payload?.title || '')
      setDetailContentDraft(payload?.content ?? '')
      setDetailCategoryDraft(payload?.category || '')
      setDetailLinkedImageIds(normalizeLinkedIds(payload?.linkedLibraryImageIds))
    } catch {
      // ignore
    }
  }

  const onApprove = async (id) => {
    if (!allow.perm([Permissions.TEMPLATE_MANAGE])) {
      show('권한이 없습니다', 'error')
      return
    }
    try {
      await api.post(`/operator/message-templates/drafts/${id}/approve`)
      show('승인되었습니다', 'success')
      await reload()
      await syncDetailFromServer(id)
    } catch (_) {
      show('승인 실패', 'error')
    }
  }

  const onReject = async (id) => {
    if (!allow.perm([Permissions.TEMPLATE_MANAGE])) {
      show('권한이 없습니다', 'error')
      return
    }
    try {
      await api.post(`/operator/message-templates/drafts/${id}/reject`)
      show('반려되었습니다', 'success')
      await reload()
      await syncDetailFromServer(id)
    } catch (_) {
      show('반려 실패', 'error')
    }
  }

  const onDelete = async (id) => {
    if (!allow.perm([Permissions.TEMPLATE_MANAGE])) {
      show('권한이 없습니다', 'error')
      return
    }
    if (!window.confirm('해당 템플릿을 삭제할까요? 삭제 후에는 복구할 수 없습니다.')) {
      return
    }
    try {
      await api.delete(`/operator/message-templates/drafts/${id}`)
      show('삭제되었습니다', 'success')
      if (detail?.id === id) setDetail(null)
      await reload()
    } catch (_) {
      show('삭제 실패', 'error')
    }
  }

  const onOpenDetail = async (id) => {
    try {
      const res = await api.get(`/operator/message-templates/drafts/${id}`)
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      setDetail(payload)
      setDetailTitleDraft(payload?.title || '')
      setDetailContentDraft(payload?.content ?? '')
      setDetailCategoryDraft(payload?.category || '')
      setDetailLinkedImageIds(normalizeLinkedIds(payload?.linkedLibraryImageIds))
    } catch (_) {
      show('상세 조회 실패', 'error')
    }
  }

  useEffect(() => {
    if (!detail?.id) {
      setLibraryImagesForPicker([])
      return
    }
    let cancelled = false
    ;(async () => {
      setLibraryPickerLoading(true)
      try {
        const res = await api.get('/gemini/images', { params: {} })
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        const arr = Array.isArray(payload) ? payload.slice(0, 80) : []
        if (!cancelled) setLibraryImagesForPicker(arr)
      } catch {
        if (!cancelled) setLibraryImagesForPicker([])
      } finally {
        if (!cancelled) setLibraryPickerLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [detail?.id])

  const onSaveDetailEdits = async () => {
    if (!detail?.id) return
    if (!allow.perm([Permissions.TEMPLATE_MANAGE])) {
      show('권한이 없습니다', 'error')
      return
    }
    const title = detailTitleDraft.trim()
    if (!title) {
      show('제목을 입력해 주세요.', 'error')
      return
    }
    setDetailEditsSaving(true)
    try {
      await api.patch(`/operator/message-templates/drafts/${detail.id}`, {
        title,
        content: detailContentDraft,
        category: detailCategoryDraft.trim() || null,
        linkedLibraryImageIds: detailLinkedImageIds,
      })
      show('저장했습니다.', 'success')
      await reload()
      const res = await api.get(`/operator/message-templates/drafts/${detail.id}`)
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      setDetail(payload)
      setDetailTitleDraft(payload?.title || '')
      setDetailContentDraft(payload?.content ?? '')
      setDetailCategoryDraft(payload?.category || '')
      setDetailLinkedImageIds(normalizeLinkedIds(payload?.linkedLibraryImageIds))
    } catch (e) {
      const msg = e?.response?.data?.message
      show(typeof msg === 'string' ? msg : '저장에 실패했습니다.', 'error')
    } finally {
      setDetailEditsSaving(false)
    }
  }

  const loadGenerationPolicies = useCallback(async () => {
    try {
      const res = await api.get('/operator/message-templates/generation-policies')
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      const list = Array.isArray(payload) ? payload : []
      setGenerationPolicies(list)
      if (generationMode === 'NEWS') {
        const p = list.find((v) => v.generationMode === 'NEWS')
        if (p?.llmModel) setGenerationLlmModel(p.llmModel)
      } else if (generationMode === 'INSURANCE_TOPIC') {
        const p = list.find((v) => v.generationMode === 'INSURANCE_TOPIC')
        if (p?.llmModel) setGenerationLlmModel(p.llmModel)
      }
    } catch (_) {
      // ignore
    }
  }, [generationMode])

  useEffect(() => {
    void loadGenerationPolicies()
  }, [loadGenerationPolicies])

  const onSaveGenerationPolicy = async (id, llmModel) => {
    if (!allow.perm([Permissions.TEMPLATE_MANAGE])) {
      show('권한이 없습니다', 'error')
      return
    }
    setGenerationPolicySavingId(id)
    try {
      await api.patch(`/operator/message-templates/generation-policies/${id}`, { llmModel })
      show('기본 생성모델 저장 완료', 'success')
      await loadGenerationPolicies()
    } catch (_) {
      show('기본 생성모델 저장 실패', 'error')
    } finally {
      setGenerationPolicySavingId(null)
    }
  }

  const onSavePolicy = async (id) => {
    const edit = policyEdits[id]
    if (!edit) return
    const errors = validatePolicyEdit(edit)
    if (errors.length > 0) {
      show('정책 입력값을 확인해주세요', 'error')
      return
    }
    if (!allow.perm([Permissions.TEMPLATE_MANAGE])) {
      show('권한이 없습니다', 'error')
      return
    }
    setPolicySavingId(id)
    try {
      await api.patch(`/operator/message-templates/topic-policies/${id}`, {
        title: edit.title,
        enabled: !!edit.enabled,
        baseWeight: Number(edit.baseWeight || 0),
        cooldownDays: Number(edit.cooldownDays || 0),
        monthWeights: parseMonthWeightText(edit.monthWeightsText),
      })
      show('정책 저장 완료', 'success')
      await loadTopicPolicies()
    } catch (_) {
      show('정책 저장 실패', 'error')
    } finally {
      setPolicySavingId(null)
    }
  }

  const rows = useMemo(() => items, [items])
  const canManageTemplates = allow.perm([Permissions.TEMPLATE_MANAGE])
  const draftRowIds = useMemo(
    () => rows.filter((t) => t.status === 'DRAFT').map((t) => t.id),
    [rows],
  )
  const selectedDraftIdSet = useMemo(() => new Set(selectedDraftIds), [selectedDraftIds])
  const selectedActionableDraftIds = useMemo(
    () => draftRowIds.filter((id) => selectedDraftIdSet.has(id)),
    [draftRowIds, selectedDraftIdSet],
  )
  const allDraftRowsSelected = draftRowIds.length > 0 && selectedActionableDraftIds.length === draftRowIds.length

  const toggleDraftRowSelection = (id, checked) => {
    setSelectedDraftIds((prev) => {
      const next = new Set(prev)
      if (checked) next.add(id)
      else next.delete(id)
      return Array.from(next)
    })
  }

  const toggleDraftPageSelection = (checked) => {
    setSelectedDraftIds((prev) => {
      const next = new Set(prev)
      draftRowIds.forEach((id) => {
        if (checked) next.add(id)
        else next.delete(id)
      })
      return Array.from(next)
    })
  }

  const onBulkDraftAction = async (action) => {
    if (!canManageTemplates) {
      show('권한이 없습니다', 'error')
      return
    }
    const ids = selectedActionableDraftIds
    if (ids.length === 0) {
      show('처리할 초안 템플릿을 선택해 주세요.', 'error')
      return
    }
    const label = action === 'approve' ? '승인' : action === 'reject' ? '반려' : '삭제'
    const suffix = action === 'delete' ? ' 삭제 후에는 복구할 수 없습니다.' : ''
    if (!window.confirm(`선택한 초안 ${ids.length}건을 일괄${label}할까요?${suffix}`)) {
      return
    }
    setBulkAction(action)
    try {
      const res = await api.post(
        `/operator/message-templates/drafts/bulk/${action}`,
        { ids },
        { headers: { 'Content-Type': 'application/json' } },
      )
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      const processed = Number(payload?.processed)
      const skipped = Number(payload?.skipped)
      const message = `일괄${label} 완료: ${Number.isFinite(processed) ? processed : ids.length}건${Number.isFinite(skipped) && skipped > 0 ? `, 제외 ${skipped}건` : ''}`
      show(message, 'success')
      const detailWasSelected = detail?.id && ids.includes(detail.id)
      if (action === 'delete' && detailWasSelected) {
        setDetail(null)
      }
      await reload()
      if (action !== 'delete' && detailWasSelected) {
        await syncDetailFromServer(detail.id)
      }
    } catch (e) {
      const msg = e?.response?.data?.message
      show(typeof msg === 'string' ? msg : `일괄${label} 실패`, 'error')
    } finally {
      setBulkAction(null)
    }
  }

  const moveLinkedImage = (index, dir) => {
    setDetailLinkedImageIds((prev) => {
      const j = index + dir
      if (j < 0 || j >= prev.length) return prev
      const next = [...prev]
      const t = next[index]
      next[index] = next[j]
      next[j] = t
      return next
    })
  }

  const removeLinkedImageAt = (index) => {
    setDetailLinkedImageIds((prev) => prev.filter((_, i) => i !== index))
  }

  const appendLinkedImageId = (imgId) => {
    const id = Number(imgId)
    if (!Number.isFinite(id) || id < 1) return
    setDetailLinkedImageIds((prev) => {
      if (prev.includes(id)) return prev
      if (prev.length >= 20) {
        show('연결은 최대 20개까지입니다.', 'error')
        return prev
      }
      return [...prev, id]
    })
  }

  return (
    <section>
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold">메시지 템플릿</h1>
            <MenuGuideButton slug="template-management" title="템플릿관리 가이드" />
          </div>
        </div>
        <div className="flex flex-col items-end gap-2 sm:flex-row sm:items-center sm:flex-wrap sm:justify-end">
          <select
            className="rounded border px-3 py-2 text-sm"
            value={generationMode}
            onChange={(e) => setGenerationMode(e.target.value)}
            title="수동 생성 모드"
          >
            <option value="BOTH">전체(뉴스+보험세부주제)</option>
            <option value="NEWS">뉴스 주제</option>
            <option value="INSURANCE_TOPIC">보험 세부주제</option>
          </select>
          <select
            className="rounded border px-3 py-2 text-sm"
            value={generationLlmModel}
            onChange={(e) => setGenerationLlmModel(e.target.value)}
            title="생성 모델"
          >
            <option value="">기본정책 사용</option>
            <option value="GEMINI_FLASH">Gemini Flash</option>
            <option value="OPENAI_4O_MINI">OpenAI 4o-mini</option>
            <option value="RULE_BASED">규칙기반(LLM 미사용)</option>
          </select>
          <button
            {...guard.disableIfNoPerm([Permissions.TEMPLATE_MANAGE])}
            className="rounded bg-brand-navy text-white px-3 py-2 text-sm disabled:opacity-50"
            onClick={onGenerate}
            disabled={generating}
          >
            {generating ? '생성 중...' : '초안 생성'}
          </button>
        </div>
      </div>

      <div className="mt-3 rounded border border-slate-200 bg-slate-50/80 overflow-hidden">
        <button
          type="button"
          className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-slate-100/80"
          onClick={() => setTopicInputsOpen((v) => !v)}
          aria-expanded={topicInputsOpen}
        >
          <div>
            <div className="text-sm font-medium text-slate-800">주제·세부주제 입력 (선택)</div>
            <div className="mt-0.5 text-xs text-slate-500">
              뉴스 주제·보험 세부주제를 직접 지정할 때 펼칩니다. 비우면 자동 선정됩니다.
            </div>
          </div>
          <span className="shrink-0 text-xs font-medium text-slate-600 tabular-nums">
            {topicInputsOpen ? '접기' : '펼치기'}
          </span>
        </button>
        {topicInputsOpen && (
          <div className="space-y-3 border-t border-slate-200 px-3 py-3">
            {(generationMode === 'NEWS' || generationMode === 'BOTH') && (
              <div className="rounded border border-slate-200 bg-white p-3">
                <label className="block text-sm font-medium text-slate-700">뉴스형 · 주제 직접입력 (선택)</label>
                <p className="mt-0.5 text-xs text-slate-500">
                  비우면 수집된 뉴스·이슈 중 자동 선정됩니다. 입력 시 해당 문구를 테마로 정보성 문자 초안 1건을 만듭니다.
                </p>
                <textarea
                  className="mt-2 w-full max-w-2xl rounded border border-slate-300 px-3 py-2 text-sm"
                  rows={3}
                  placeholder="예: 비급여 당뇨 치료제 청구 유의사항"
                  value={customNewsTheme}
                  onChange={(e) => setCustomNewsTheme(e.target.value)}
                  maxLength={500}
                />
              </div>
            )}

            {(generationMode === 'INSURANCE_TOPIC' || generationMode === 'BOTH') && (
              <div className="rounded border border-slate-200 bg-white p-3">
                <label className="block text-sm font-medium text-slate-700">보험 세부주제 · 직접입력 (선택)</label>
                <p className="mt-0.5 text-xs text-slate-500">
                  제목만 입력해도 됩니다. 비우면 아래 「보험 세부주제 정책」에 따른 자동 주제가 사용됩니다.
                </p>
                <input
                  type="text"
                  className="mt-2 w-full max-w-2xl rounded border border-slate-300 px-3 py-2 text-sm"
                  placeholder="예: 운전자보험 교통사고 처리"
                  value={customInsuranceTopic}
                  onChange={(e) => setCustomInsuranceTopic(e.target.value)}
                  maxLength={200}
                />
                <label className="mt-3 block text-xs font-medium text-slate-600">추가 각도·안내 포인트 (선택)</label>
                <textarea
                  className="mt-1 w-full max-w-2xl rounded border border-slate-300 px-3 py-2 text-sm"
                  rows={2}
                  placeholder="LLM 참고용: 강조할 실무 포인트, 금지할 표현 등"
                  value={customInsuranceTopicHint}
                  onChange={(e) => setCustomInsuranceTopicHint(e.target.value)}
                  maxLength={2000}
                />
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mt-4 rounded border border-indigo-200 bg-indigo-50/40 overflow-hidden">
        <button
          type="button"
          className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left hover:bg-indigo-50"
          onClick={() => setAiContentOpen((v) => !v)}
          aria-expanded={aiContentOpen}
        >
          <div>
            <h2 className="text-sm font-semibold text-indigo-950">AI 콘텐츠 초안 생성 (카드뉴스·전단지·블로그)</h2>
            <p className="mt-0.5 text-xs text-indigo-900/70">
              운영자 검수용 초안을 생성합니다. 생성 결과는 기존 메시지 템플릿처럼 편집·승인·이미지 연결이 가능합니다.
            </p>
          </div>
          <span className="shrink-0 text-xs font-medium text-indigo-900 tabular-nums">
            {aiContentOpen ? '접기' : '펼치기'}
          </span>
        </button>
        {aiContentOpen && (
          <div className="border-t border-indigo-100 px-4 pb-4 pt-3">
            <div className="grid gap-3 md:grid-cols-3">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">콘텐츠 유형</label>
                <select
                  className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                  value={aiContentType}
                  onChange={(e) => setAiContentType(e.target.value)}
                  {...guard.disableIfNoPerm([Permissions.TEMPLATE_MANAGE])}
                >
                  <option value="CARD_NEWS">카드뉴스</option>
                  <option value="FLYER">전단지</option>
                  <option value="BLOG">블로그 초안</option>
                  <option value="LANDING">랜딩 섹션</option>
                  <option value="MESSAGE">문자/카카오</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">생성 모델</label>
                <select
                  className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                  value={aiContentLlmModel}
                  onChange={(e) => setAiContentLlmModel(e.target.value)}
                  {...guard.disableIfNoPerm([Permissions.TEMPLATE_MANAGE])}
                >
                  <option value="">Gemini Flash</option>
                  <option value="GEMINI_FLASH">Gemini Flash</option>
                  <option value="OPENAI_4O_MINI">OpenAI 4o-mini</option>
                  <option value="RULE_BASED">규칙기반(LLM 미사용)</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">대상 연령/세그먼트</label>
                <input
                  type="text"
                  className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                  value={aiContentTargetAgeGroup}
                  onChange={(e) => setAiContentTargetAgeGroup(e.target.value)}
                  placeholder="예: 40대, 부모님 고객, 신규상담 고객"
                  maxLength={80}
                  {...guard.disableIfNoPerm([Permissions.TEMPLATE_MANAGE])}
                />
              </div>
              <div className="md:col-span-3">
                <label className="block text-xs font-medium text-slate-700 mb-1">주제</label>
                <input
                  type="text"
                  className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                  value={aiContentTopic}
                  onChange={(e) => setAiContentTopic(e.target.value)}
                  placeholder="예: 실손 청구 서류 누락 방지, 운전자보험 교통사고 처리지원금"
                  maxLength={200}
                  {...guard.disableIfNoPerm([Permissions.TEMPLATE_MANAGE])}
                />
              </div>
              <div className="md:col-span-3">
                <label className="block text-xs font-medium text-slate-700 mb-1">상황/맥락</label>
                <textarea
                  className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                  rows={2}
                  value={aiContentTargetContext}
                  onChange={(e) => setAiContentTargetContext(e.target.value)}
                  placeholder="예: 병원 진료 후 보험금 청구를 안내하는 콘텐츠"
                  maxLength={500}
                  {...guard.disableIfNoPerm([Permissions.TEMPLATE_MANAGE])}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">톤</label>
                <input
                  type="text"
                  className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                  value={aiContentTone}
                  onChange={(e) => setAiContentTone(e.target.value)}
                  maxLength={120}
                  {...guard.disableIfNoPerm([Permissions.TEMPLATE_MANAGE])}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">채널</label>
                <input
                  type="text"
                  className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                  value={aiContentChannel}
                  onChange={(e) => setAiContentChannel(e.target.value)}
                  maxLength={80}
                  {...guard.disableIfNoPerm([Permissions.TEMPLATE_MANAGE])}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">행동 유도</label>
                <input
                  type="text"
                  className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                  value={aiContentCallToAction}
                  onChange={(e) => setAiContentCallToAction(e.target.value)}
                  placeholder="예: 서류 사진을 보내달라는 안내"
                  maxLength={300}
                  {...guard.disableIfNoPerm([Permissions.TEMPLATE_MANAGE])}
                />
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                {...guard.disableIfNoPerm([Permissions.TEMPLATE_MANAGE])}
                className="rounded bg-indigo-700 px-3 py-2 text-sm text-white hover:bg-indigo-800 disabled:opacity-50"
                onClick={() => void onGenerateAiContentDraft()}
                disabled={aiContentGenerating}
              >
                {aiContentGenerating ? '콘텐츠 생성 중...' : 'AI 콘텐츠 초안 생성'}
              </button>
              <span className="text-xs text-slate-500">생성 후 상세 패널에서 문구를 수정하고 승인하세요.</span>
            </div>
          </div>
        )}
      </div>

      <div className="mt-4 rounded border border-slate-200 bg-white overflow-hidden">
        <button
          type="button"
          className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left hover:bg-slate-50"
          onClick={() => setManualDraftOpen((v) => !v)}
          aria-expanded={manualDraftOpen}
        >
          <div>
            <h2 className="text-sm font-semibold text-slate-800">직접 작성 (AI 없이 초안 등록)</h2>
            <p className="mt-0.5 text-xs text-slate-500">
              제목·유형·본문을 직접 입력해 초안을 등록합니다. 저장 후 목록에서 승인·편집할 수 있습니다.
            </p>
          </div>
          <span className="shrink-0 text-xs font-medium text-slate-600 tabular-nums">
            {manualDraftOpen ? '접기' : '펼치기'}
          </span>
        </button>
        {manualDraftOpen && (
          <div className="border-t border-slate-200 px-4 pb-4 pt-1">
            <div className="mt-2 grid gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-slate-600 mb-1">제목</label>
                <input
                  type="text"
                  className="w-full max-w-2xl rounded border border-slate-300 px-3 py-2 text-sm"
                  value={manualTitle}
                  onChange={(e) => setManualTitle(e.target.value)}
                  maxLength={200}
                  placeholder="예: 건강검진 전 체크리스트 안내"
                  {...guard.disableIfNoPerm([Permissions.TEMPLATE_MANAGE])}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">유형</label>
                <select
                  className="w-full max-w-xs rounded border border-slate-300 px-3 py-2 text-sm"
                  value={manualTemplateType}
                  onChange={(e) => setManualTemplateType(e.target.value)}
                  {...guard.disableIfNoPerm([Permissions.TEMPLATE_MANAGE])}
                >
                  <option value="INFO">정보형</option>
                  <option value="CONSULT">상담유도형</option>
                  <option value="REMIND">리마인드형</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">카테고리 (선택)</label>
                <input
                  type="text"
                  className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                  value={manualCategory}
                  onChange={(e) => setManualCategory(e.target.value)}
                  maxLength={100}
                  placeholder="예: 실비, 연금"
                  {...guard.disableIfNoPerm([Permissions.TEMPLATE_MANAGE])}
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-slate-600 mb-1">본문</label>
                <textarea
                  className="w-full min-h-[160px] rounded border border-slate-300 px-3 py-2 text-sm font-mono whitespace-pre-wrap"
                  value={manualContent}
                  onChange={(e) => setManualContent(e.target.value)}
                  placeholder="발송에 사용할 문구를 그대로 입력합니다."
                  {...guard.disableIfNoPerm([Permissions.TEMPLATE_MANAGE])}
                />
              </div>
            </div>
            <div className="mt-3">
              <button
                type="button"
                {...guard.disableIfNoPerm([Permissions.TEMPLATE_MANAGE])}
                className="rounded border border-brand-navy bg-white px-3 py-2 text-sm text-brand-navy hover:bg-slate-50 disabled:opacity-50"
                onClick={() => void onCreateManualDraft()}
                disabled={manualSaving}
              >
                {manualSaving ? '등록 중...' : '직접 작성 초안 저장'}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <select
          className="w-56 rounded border px-3 py-2 text-sm"
          value={status}
          onChange={(e) => {
            setListPage(0)
            setStatus(e.target.value)
          }}
        >
          <option value="">전체 상태</option>
          <option value="DRAFT">초안</option>
          <option value="APPROVED">승인</option>
          <option value="REJECTED">반려</option>
        </select>
        <button className="rounded border px-3 py-2 text-sm hover:bg-slate-50" onClick={() => reload()}>
          새로고침
        </button>
        <span {...guard.hideIfNoPerm([Permissions.TEMPLATE_MANAGE])} className="inline-flex flex-wrap items-center gap-2">
          <span className="text-sm text-slate-600">선택 {selectedActionableDraftIds.length}건</span>
          <button
            type="button"
            className="rounded border border-emerald-300 px-3 py-2 text-sm text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
            disabled={!canManageTemplates || selectedActionableDraftIds.length === 0 || bulkAction !== null || loading}
            onClick={() => void onBulkDraftAction('approve')}
          >
            {bulkAction === 'approve' ? '승인 중...' : '일괄승인'}
          </button>
          <button
            type="button"
            className="rounded border border-red-300 px-3 py-2 text-sm text-red-700 hover:bg-red-50 disabled:opacity-50"
            disabled={!canManageTemplates || selectedActionableDraftIds.length === 0 || bulkAction !== null || loading}
            onClick={() => void onBulkDraftAction('reject')}
          >
            {bulkAction === 'reject' ? '반려 중...' : '일괄반려'}
          </button>
          <button
            type="button"
            className="rounded border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            disabled={!canManageTemplates || selectedActionableDraftIds.length === 0 || bulkAction !== null || loading}
            onClick={() => void onBulkDraftAction('delete')}
          >
            {bulkAction === 'delete' ? '삭제 중...' : '일괄삭제'}
          </button>
        </span>
      </div>
      {lastFailureSummary && Object.keys(lastFailureSummary).length > 0 && (
        <div className="mt-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <span className="font-semibold">수집 실패 요약:</span>{' '}
          {Object.entries(lastFailureSummary).map(([k, v]) => `${k} ${v}건`).join(' / ')}
        </div>
      )}

      <div className="mt-6 rounded border">
        <ListContainer
          loading={loading}
          error={error}
          onRetry={() => reload()}
          header={null}
          table={(
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="w-10 px-3 py-2 text-left">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-slate-300"
                        aria-label="현재 페이지 초안 전체 선택"
                        checked={allDraftRowsSelected}
                        disabled={!canManageTemplates || draftRowIds.length === 0}
                        onChange={(e) => toggleDraftPageSelection(e.target.checked)}
                      />
                    </th>
                    <th className="px-3 py-2 text-left">제목</th>
                    <th className="px-3 py-2 text-left">카테고리</th>
                    <th className="px-3 py-2 text-left">유형</th>
                    <th className="px-3 py-2 text-left">상태</th>
                    <th className="px-3 py-2 text-left">
                      <span title="금감원(FSS) 수집/파싱 성공 여부 (성공: O, 실패 fallback: X)">금감원체크</span>
                    </th>
                    <th className="px-3 py-2 text-left">생성일</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((t) => (
                    <tr key={t.id} className="border-t">
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-slate-300 disabled:opacity-40"
                          aria-label={`${t.title || '메시지 템플릿'} 선택`}
                          checked={selectedDraftIdSet.has(t.id)}
                          disabled={!canManageTemplates || t.status !== 'DRAFT'}
                          onChange={(e) => toggleDraftRowSelection(t.id, e.target.checked)}
                        />
                      </td>
                      <td className="px-3 py-2">{t.title}</td>
                      <td className="px-3 py-2 text-slate-600">{t.category || '—'}</td>
                      <td className="px-3 py-2">{typeLabel(t.templateType)}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${t.status === 'APPROVED'
                            ? 'bg-emerald-100 text-emerald-700'
                            : t.status === 'REJECTED'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-slate-100 text-slate-700'
                          }`}
                        >
                          {statusLabel(t.status)}
                        </span>
                      </td>
                      <td className="px-3 py-2">{t.degradedMode ? 'X' : 'O'}</td>
                      <td className="px-3 py-2">{formatDateTimeMinute(t.createdAt)}</td>
                      <td className="px-3 py-2 text-right space-x-2 whitespace-nowrap">
                        <button className="rounded border px-2 py-1 text-brand-navy hover:bg-slate-50" onClick={() => onOpenDetail(t.id)}>상세</button>
                        <span {...guard.hideIfNoPerm([Permissions.TEMPLATE_MANAGE])} className="inline-flex flex-wrap gap-1 justify-end">
                          {t.status !== 'APPROVED' && (
                            <button type="button" className="text-emerald-700 hover:underline text-xs" onClick={() => onApprove(t.id)}>승인</button>
                          )}
                          {t.status !== 'REJECTED' && (
                            <button type="button" className="text-red-700 hover:underline text-xs" onClick={() => onReject(t.id)}>반려</button>
                          )}
                          <button type="button" className="text-slate-700 hover:underline text-xs" onClick={() => onDelete(t.id)}>삭제</button>
                        </span>
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr>
                      <td className="px-3 py-8 text-center text-slate-500" colSpan={8}>데이터가 없습니다.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          footer={(
            <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-t bg-slate-50 text-sm text-slate-700">
              <span className="tabular-nums">
                총 {listTotalElements}건
                {listTotalPages > 0 ? ` · ${listPage + 1} / ${listTotalPages}페이지` : ''}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="rounded border px-2 py-1 text-xs hover:bg-white disabled:opacity-40"
                  disabled={listPage <= 0 || loading}
                  onClick={() => setListPage((p) => Math.max(0, p - 1))}
                >
                  이전
                </button>
                <button
                  type="button"
                  className="rounded border px-2 py-1 text-xs hover:bg-white disabled:opacity-40"
                  disabled={listTotalPages <= 0 || listPage >= listTotalPages - 1 || loading}
                  onClick={() => setListPage((p) => p + 1)}
                >
                  다음
                </button>
              </div>
            </div>
          )}
        />
      </div>

      <div className="mt-8 rounded border p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">생성 모델 기본정책</h2>
          <button className="rounded border px-3 py-1.5 text-sm hover:bg-slate-50" onClick={() => loadGenerationPolicies()}>
            기본정책 새로고침
          </button>
        </div>
        <p className="mt-1 text-xs text-slate-500">수동 생성에서 모델을 고르지 않을 때 사용할 기본 모델입니다. (뉴스/보험세부주제 별도 설정)</p>
        <div className="mt-3 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left">생성모드</th>
                <th className="px-3 py-2 text-left">기본 모델</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {generationPolicies.map((p) => (
                <tr key={p.id} className="border-t">
                  <td className="px-3 py-2">{p.generationMode === 'NEWS' ? '뉴스 주제' : '보험 세부주제'}</td>
                  <td className="px-3 py-2">
                    <select
                      className="rounded border px-2 py-1"
                      value={p.llmModel || 'GEMINI_FLASH'}
                      onChange={(e) => {
                        const value = e.target.value
                        setGenerationPolicies((prev) => prev.map((x) => x.id === p.id ? { ...x, llmModel: value } : x))
                      }}
                    >
                      <option value="GEMINI_FLASH">Gemini Flash</option>
                      <option value="OPENAI_4O_MINI">OpenAI 4o-mini</option>
                      <option value="RULE_BASED">규칙기반(LLM 미사용)</option>
                    </select>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      {...guard.disableIfNoPerm([Permissions.TEMPLATE_MANAGE])}
                      className="rounded border px-2 py-1 hover:bg-slate-50 disabled:opacity-50"
                      onClick={() => onSaveGenerationPolicy(p.id, p.llmModel || 'GEMINI_FLASH')}
                      disabled={generationPolicySavingId === p.id}
                    >
                      {generationPolicySavingId === p.id ? '저장중...' : '저장'}
                    </button>
                  </td>
                </tr>
              ))}
              {generationPolicies.length === 0 && (
                <tr><td className="px-3 py-6 text-center text-slate-500" colSpan={3}>기본정책 데이터가 없습니다.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-8 rounded border p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">보험 세부주제 정책</h2>
          <button className="rounded border px-3 py-1.5 text-sm hover:bg-slate-50" onClick={() => loadTopicPolicies()}>
            정책 새로고침
          </button>
        </div>
        <p className="mt-1 text-xs text-slate-500">재기동 없이 활성화/가중치/중복회피일을 조정할 수 있습니다. 월가중치는 `월:가중치` 형식으로 입력하세요. (예: `1:2,9:2`)</p>
        <div className="mt-3 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left">코드</th>
                <th className="px-3 py-2 text-left">제목</th>
                <th className="px-3 py-2 text-left">활성</th>
                <th className="px-3 py-2 text-left">기본가중치</th>
                <th className="px-3 py-2 text-left">중복회피일</th>
                <th className="px-3 py-2 text-left">월가중치</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {policyLoading && (
                <tr><td className="px-3 py-6 text-center text-slate-500" colSpan={7}>불러오는 중...</td></tr>
              )}
              {!policyLoading && topicPolicies.map((p) => {
                const edit = policyEdits[p.id] || {}
                const errors = validatePolicyEdit(edit)
                return (
                  <React.Fragment key={p.id}>
                    <tr key={p.id} className="border-t">
                      <td className="px-3 py-2">{p.topicCode}</td>
                      <td className="px-3 py-2">
                        <input
                          className={`w-44 rounded border px-2 py-1 ${errors.length > 0 ? 'border-red-300' : ''}`}
                          value={edit.title ?? ''}
                          onChange={(e) => setPolicyEdits((prev) => ({ ...prev, [p.id]: { ...prev[p.id], title: e.target.value } }))}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={!!edit.enabled}
                          onChange={(e) => setPolicyEdits((prev) => ({ ...prev, [p.id]: { ...prev[p.id], enabled: e.target.checked } }))}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          className={`w-24 rounded border px-2 py-1 ${errors.length > 0 ? 'border-red-300' : ''}`}
                          value={edit.baseWeight ?? 0}
                          onChange={(e) => setPolicyEdits((prev) => ({ ...prev, [p.id]: { ...prev[p.id], baseWeight: e.target.value } }))}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          className={`w-24 rounded border px-2 py-1 ${errors.length > 0 ? 'border-red-300' : ''}`}
                          value={edit.cooldownDays ?? 7}
                          onChange={(e) => setPolicyEdits((prev) => ({ ...prev, [p.id]: { ...prev[p.id], cooldownDays: e.target.value } }))}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          className={`w-56 rounded border px-2 py-1 ${errors.length > 0 ? 'border-red-300' : ''}`}
                          value={edit.monthWeightsText ?? ''}
                          onChange={(e) => setPolicyEdits((prev) => ({ ...prev, [p.id]: { ...prev[p.id], monthWeightsText: e.target.value } }))}
                          placeholder="1:2,9:2"
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          {...guard.disableIfNoPerm([Permissions.TEMPLATE_MANAGE])}
                          className="rounded border px-2 py-1 hover:bg-slate-50 disabled:opacity-50"
                          onClick={() => onSavePolicy(p.id)}
                          disabled={policySavingId === p.id || errors.length > 0}
                        >
                          {policySavingId === p.id ? '저장중...' : '저장'}
                        </button>
                      </td>
                    </tr>
                    {errors.length > 0 && (
                      <tr className="border-t">
                        <td className="px-3 py-2 text-xs text-red-600" colSpan={7}>
                          {errors.join(' ')}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              })}
              {!policyLoading && topicPolicies.length === 0 && (
                <tr><td className="px-3 py-6 text-center text-slate-500" colSpan={7}>정책 데이터가 없습니다.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {detail && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-50" onClick={() => setDetail(null)}>
          <div className="w-full max-w-3xl rounded bg-white p-6 max-h-[85vh] overflow-auto shadow-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm font-semibold text-slate-800">템플릿 상세</div>
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="rounded bg-slate-100 px-2 py-1">{statusLabel(detail.status)}</span>
                <span className="rounded bg-slate-100 px-2 py-1">{typeLabel(detail.templateType)}</span>
                <span className={`rounded px-2 py-1 ${detail.degradedMode ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                  금감원체크: {detail.degradedMode ? 'X' : 'O'}
                </span>
                <span
                  className={`rounded px-2 py-1 ${isFallbackDraft(detail) ? 'bg-amber-100 text-amber-800' : 'bg-sky-100 text-sky-800'}`}
                  title={isFallbackDraft(detail) ? '수집 이슈 부족/실패로 생성된 기본 안내 초안' : '수집된 실제 이슈를 기반으로 생성된 초안'}
                >
                  {isFallbackDraft(detail) ? 'fallback 초안' : '실수집 초안'}
                </span>
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-slate-700 mb-1">제목</label>
              <input
                type="text"
                className="w-full rounded border px-3 py-2 text-sm"
                value={detailTitleDraft}
                onChange={(e) => setDetailTitleDraft(e.target.value)}
                maxLength={200}
                {...guard.disableIfNoPerm([Permissions.TEMPLATE_MANAGE])}
              />
            </div>

            <div className="mt-3 rounded border border-slate-200 p-3">
              <div className="font-semibold text-sm mb-1">카테고리 (단체발송·외부프로그램 필터용)</div>
              <p className="text-xs text-slate-500 mb-2">예: 실비, 연금, 입원. 비우면 미분류로 표시됩니다.</p>
              <input
                type="text"
                className="w-full rounded border px-2 py-1.5 text-sm"
                value={detailCategoryDraft}
                onChange={(e) => setDetailCategoryDraft(e.target.value)}
                maxLength={100}
                placeholder="카테고리 입력"
                {...guard.disableIfNoPerm([Permissions.TEMPLATE_MANAGE])}
              />
            </div>

            <div className="mt-4 rounded border border-slate-200 p-3">
              <div className="font-semibold text-sm mb-1">이미지 자료실 매핑</div>
              <p className="text-xs text-slate-500 mb-2">
                메시지 템플릿에서 발송할 카드뉴스 등 이미지 자료실 항목 ID를 순서대로 연결합니다. 자료실 캡션과 별도이며, 저장은 아래 &quot;저장&quot; 버튼과 함께 반영됩니다.
              </p>
              {detailLinkedImageIds.length === 0 ? (
                <div className="text-xs text-slate-500 mb-2">연결된 이미지 없음</div>
              ) : (
                <>
                  {detailLinkedImageIds.length > 1 && (
                    <button
                      type="button"
                      className="mb-2 text-xs font-medium text-brand-navy hover:underline"
                      onClick={() => setMappingImageModalIds([...detailLinkedImageIds])}
                    >
                      연결된 이미지 전체 미리보기
                    </button>
                  )}
                  <ul className="space-y-1.5 mb-3">
                    {detailLinkedImageIds.map((imgId, idx) => (
                      <li
                        key={`${imgId}-${idx}`}
                        className="flex flex-col gap-1 rounded bg-slate-50 px-2 py-1.5 text-xs"
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-mono text-slate-800">#{imgId}</span>
                          <span className="text-slate-500">순서 {idx + 1}</span>
                          <button
                            type="button"
                            className="rounded border border-slate-300 bg-white px-2 py-0.5 text-[11px] text-slate-700 hover:bg-slate-100"
                            onClick={() => setMappingImageModalIds([imgId])}
                          >
                            미리보기
                          </button>
                          <span {...guard.hideIfNoPerm([Permissions.TEMPLATE_MANAGE])} className="inline-flex flex-wrap gap-1 sm:ml-auto">
                            <button
                              type="button"
                              className="rounded border px-1.5 py-0.5 hover:bg-white disabled:opacity-40"
                              disabled={idx === 0}
                              onClick={() => moveLinkedImage(idx, -1)}
                            >
                              ↑
                            </button>
                            <button
                              type="button"
                              className="rounded border px-1.5 py-0.5 hover:bg-white disabled:opacity-40"
                              disabled={idx === detailLinkedImageIds.length - 1}
                              onClick={() => moveLinkedImage(idx, 1)}
                            >
                              ↓
                            </button>
                            <button
                              type="button"
                              className="rounded border border-red-200 px-1.5 py-0.5 text-red-700 hover:bg-red-50"
                              onClick={() => removeLinkedImageAt(idx)}
                            >
                              제거
                            </button>
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              <div {...guard.hideIfNoPerm([Permissions.TEMPLATE_MANAGE])}>
                <div className="text-xs font-medium text-slate-600 mb-1">자료실에서 추가 (최근 80건)</div>
                {libraryPickerLoading ? (
                  <div className="text-xs text-slate-500 py-2">불러오는 중…</div>
                ) : (
                  <div className="max-h-40 overflow-y-auto rounded border bg-white divide-y">
                    {libraryImagesForPicker.length === 0 ? (
                      <div className="p-2 text-xs text-slate-500">이미지가 없거나 목록을 불러오지 못했습니다.</div>
                    ) : (
                      libraryImagesForPicker.map((img) => {
                        const id = img?.id
                        const already = detailLinkedImageIds.includes(id)
                        return (
                          <div key={id} className="flex items-start gap-2 px-2 py-1.5 text-xs">
                            <div className="min-w-0 flex-1">
                              <span className="font-mono text-slate-800">#{id}</span>
                              <span className="text-slate-500"> · {img?.category || '-'}</span>
                              {img?.caption ? (
                                <div className="text-slate-600 line-clamp-2 mt-0.5 whitespace-pre-wrap">{img.caption}</div>
                              ) : null}
                            </div>
                            <div className="shrink-0 flex flex-col gap-1 items-end">
                              <button
                                type="button"
                                className="rounded border px-2 py-0.5 text-[11px] hover:bg-slate-50"
                                onClick={() => setMappingImageModalIds([id])}
                              >
                                미리보기
                              </button>
                              <button
                                type="button"
                                className="rounded border px-2 py-0.5 hover:bg-slate-50 disabled:opacity-40"
                                disabled={already || detailLinkedImageIds.length >= 20}
                                onClick={() => appendLinkedImageId(id)}
                              >
                                {already ? '추가됨' : '추가'}
                              </button>
                            </div>
                          </div>
                        )
                      })
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-slate-700 mb-1">본문</label>
              <textarea
                className="w-full min-h-[200px] rounded border px-3 py-2 text-sm font-mono whitespace-pre-wrap"
                value={detailContentDraft}
                onChange={(e) => setDetailContentDraft(e.target.value)}
                {...guard.disableIfNoPerm([Permissions.TEMPLATE_MANAGE])}
              />
              <p className="mt-1 text-xs text-slate-500">승인·반려·초안 상태 모두 편집 가능합니다. (삭제된 템플릿은 목록에 없습니다.)</p>
            </div>

            <div className="mt-4">
              <div className="font-semibold text-sm mb-2">출처</div>
              <div className="space-y-2">
                {(Array.isArray(detail.sources) ? detail.sources : []).map((s, idx) => (
                  <div key={idx} className="rounded border p-2">
                    <div className="text-sm font-medium">{s.sourceTitle || '-'}</div>
                    <div className="text-xs text-slate-500 break-all">{s.sourceUrl || '-'}</div>
                  </div>
                ))}
                {(!detail.sources || detail.sources.length === 0) && (
                  <div className="text-sm text-slate-500">출처 없음</div>
                )}
              </div>
            </div>

            <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
              <div className="flex flex-wrap gap-2">
                <span {...guard.hideIfNoPerm([Permissions.TEMPLATE_MANAGE])} className="inline-flex flex-wrap gap-2">
                  {detail.status !== 'APPROVED' && (
                    <button
                      type="button"
                      className="rounded bg-emerald-600 text-white px-3 py-1.5 text-sm hover:bg-emerald-700"
                      onClick={() => void onApprove(detail.id)}
                    >
                      승인
                    </button>
                  )}
                  {detail.status !== 'REJECTED' && (
                    <button
                      type="button"
                      className="rounded border border-red-300 text-red-700 px-3 py-1.5 text-sm hover:bg-red-50"
                      onClick={() => void onReject(detail.id)}
                    >
                      반려
                    </button>
                  )}
                  <button
                    type="button"
                    className="rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                    onClick={() => void onDelete(detail.id)}
                  >
                    삭제
                  </button>
                </span>
              </div>
              <div className="flex flex-wrap gap-2 justify-end">
                <button
                  type="button"
                  {...guard.disableIfNoPerm([Permissions.TEMPLATE_MANAGE])}
                  className="rounded bg-brand-navy text-white px-4 py-2 text-sm hover:bg-brand-navy/90 disabled:opacity-50"
                  onClick={() => void onSaveDetailEdits()}
                  disabled={detailEditsSaving}
                >
                  {detailEditsSaving ? '저장 중...' : '제목·본문·카테고리·이미지 매핑 저장'}
                </button>
                <button type="button" className="rounded border px-4 py-2 text-sm" onClick={() => setDetail(null)}>닫기</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {mappingImageModalIds && mappingImageModalIds.length > 0 && (
        <LibraryMappedImagesModal
          imageIds={mappingImageModalIds}
          title="이미지 자료실 미리보기"
          onClose={() => setMappingImageModalIds(null)}
        />
      )}
    </section>
  )
}
